import React from 'react';
import { AlertTriangle, CheckCircle2, CircleAlert, Database, FlaskConical, Loader2, PencilLine } from 'lucide-react';
import { Ring } from '../gameplan/jarvis/Ring';
import { Panel, Section, Sub, Tile, Slider, NumInput } from './GrowthBits';
import { money, num } from './growthData';

const MODES = [
  { k: 'example', label: 'Example', icon: FlaskConical },
  { k: 'manual', label: 'Manual', icon: PencilLine },
  { k: 'live', label: 'Live · Game Plan', icon: Database, cls: 'live' },
];

const ModeSwitch = ({ mode, onChange }) => (
  <div className="egt-modes" role="group" aria-label="Data source" data-testid="egt-mode-switch">
    {MODES.map(({ k, label, icon: Icon, cls }) => (
      <button key={k} type="button" className={cls} aria-pressed={mode === k} onClick={() => onChange(k)} data-testid={`egt-mode-${k}`}><Icon />{label}</button>
    ))}
  </div>
);

const toneFor = (actual, target, higherBetter = true) => { if (target == null || actual == null) return undefined; const r = actual / target; if (higherBetter) return r >= 1 ? 'ok' : r >= 0.85 ? 'mid' : 'bad'; return r <= 1 ? 'ok' : r <= 1.15 ? 'mid' : 'bad'; };

export default function CommandTab({ S, D, live, liveErr, update }) {
  const src = S.mode === 'live' ? 'Live' : S.mode === 'example' ? 'Example' : 'Manual';
  if (S.mode === 'live' && !live && !liveErr) {
    return (
      <div data-testid="egt-tab-command">
        <Section eyebrow="Executive Growth Command Centre" title="Goal → position → progress → bottlenecks → next actions" right={<ModeSwitch mode={S.mode} onChange={(m) => update({ mode: m })} />} />
        <div className="flex items-center justify-center h-64 gap-3" data-testid="egt-live-loading"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--fg-3)' }} /><span style={{ color: 'var(--fg-2)', fontSize: 13 }}>Pulling this month's actuals from the Game Plan Tracker…</span></div>
      </div>
    );
  }
  const pct40 = Math.round(D.p40 * 100);
  const onTrackTone = D.p40 >= 0.85 ? 'green' : D.p40 >= 0.6 ? 'amber' : 'red';
  const tiles = [
    { label: 'Lodgements / month', value: num(D.actual.lodge), sub: <>target <b>{num(D.target.lodge)}</b></>, tone: toneFor(D.actual.lodge, D.target.lodge), src },
    { label: 'Weekly lodgement $', value: money(D.actual.week), sub: <>target <b>{money(D.target.week)}</b></>, tone: toneFor(D.actual.week, D.target.week), src },
    { label: 'Incoming leads / mo', value: num(D.actual.leads), sub: <>target <b>{num(D.target.leads)}</b></>, tone: toneFor(D.actual.leads, D.target.leads), src },
    { label: 'Portal completion', value: D.actual.portal == null ? '—' : `${Math.round(D.actual.portal)}%`, sub: D.actual.portal == null ? 'no portals sent this month' : <>target <b>{D.target.portal}%</b></>, tone: toneFor(D.actual.portal, D.target.portal), src },
    { label: 'Approvals / month', value: num(D.actual.appr), sub: <>target <b>{num(D.target.appr)}</b></>, tone: toneFor(D.actual.appr, D.target.appr), src: 'Calc' },
    { label: 'Settlements / month', value: num(D.actual.settle), sub: <>target <b>{num(D.target.settle)}</b></>, tone: toneFor(D.actual.settle, D.target.settle), src: 'Calc' },
    { label: 'Cost per acq. client', value: D.actual.cac != null ? money(D.actual.cac) : '—', sub: 'paid share × cost per lead', src: 'Calc' },
    { label: 'Est. monthly revenue', value: money(D.actual.rev), sub: 'upfront + trail', src: 'Calc', tone: 'ok' },
  ];
  return (
    <div data-testid="egt-tab-command">
      <Section eyebrow="Executive Growth Command Centre" title="Goal → position → progress → bottlenecks → next actions" right={<ModeSwitch mode={S.mode} onChange={(m) => update({ mode: m })} />} />

      {S.mode === 'example' && (
        <div className="egt-trig t-amber" style={{ marginTop: 18 }} data-testid="egt-example-banner"><i><AlertTriangle /></i><div><b>Example figures shown</b><span>Current-performance numbers are illustrative placeholders, not live data. Switch to <strong>Manual</strong> to enter your actuals, or <strong>Live · Game Plan</strong> to pull this month's lodgements, leads and portal completion straight from DerwentAI.</span></div></div>
      )}
      {S.mode === 'live' && liveErr && (
        <div className="egt-trig t-red" style={{ marginTop: 18 }} data-testid="egt-live-error"><i><CircleAlert /></i><div><b>Live data unavailable</b><span>{liveErr}</span></div></div>
      )}

      <div className="egt-ceo">
        <div className="egt-q t-cyan" style={{ animationDelay: '0ms' }} data-testid="egt-q1"><small>1 · Where are we today?</small><b>${(+D.cur.lodgeM).toFixed(D.cur.lodgeM % 1 ? 1 : 0)}M</b><span>monthly lodgements (current)</span></div>
        <div className="egt-q t-violet" style={{ animationDelay: '70ms' }}><small>2 · Where to?</small><b>$40M</b><span>then $50M+/month</span></div>
        <div className={`egt-q t-${onTrackTone}`} style={{ animationDelay: '140ms' }} data-testid="egt-q3"><small>3 · On track?</small><b className="tone">{pct40}%</b><span>of the way to the $40M target</span></div>
        <div className="egt-q t-amber" style={{ animationDelay: '210ms' }}><small>4 · What's stopping us?</small><p>{D.bottleneck}</p></div>
        <div className="egt-q t-green" style={{ animationDelay: '280ms' }}><small>5 · What's next?</small><p>{D.next}</p></div>
      </div>

      <Sub>Progress to target</Sub>
      <Panel testid="egt-progress-panel">
        <div className="egt-hero">
          <div className="egt-rings">
            <Ring p={D.p40} value={`${pct40}%`} of="to $40M" label="$40M / month" sub={`<b>$${D.cur.lodgeM}M</b> of $40M`} tone={D.p40 >= 1 ? 'green' : D.p40 >= 0.85 ? 'blue' : D.p40 >= 0.6 ? 'amber' : 'red'} size={168} testid="egt-ring-40" />
            <Ring p={D.p50} value={`${Math.round(D.p50 * 100)}%`} of="to $50M" label="$50M / month" sub={`<b>$${D.cur.lodgeM}M</b> of $50M`} tone={D.p50 >= 1 ? 'green' : 'cyan'} size={168} testid="egt-ring-50" />
          </div>
          <div className="gpj-facts">
            <div className="gpj-fact headline">
              <div className="gpj-big">{money(D.cur.lodgeM * 1e6)}<span style={{ fontSize: 16, fontWeight: 600, color: 'var(--fg-3)', marginLeft: 8 }}>/ month</span></div>
              <div className="gpj-big-sub">Current lodgement run-rate · {src.toLowerCase()} data</div>
              <div className="gpj-track" style={{ marginTop: 14 }}><div className={`gpj-fill ${D.p40 >= 1 ? '' : 'blue'}`} style={{ width: `${pct40}%` }} /></div>
              <div className="gpj-cap"><span>$0</span><span>$40M</span></div>
            </div>
            <div className={`gpj-fact remain ${D.gap <= 0 ? 'done' : ''}`}><span>Gap to $40M</span><strong data-testid="egt-gap">{D.gap <= 0 ? 'Target met' : money(D.gap * 1e6) + ' / mo'}</strong></div>
            <div className="gpj-fact"><span>Weekly run-rate required</span><strong>{money(D.target.week)}</strong></div>
            <div className="gpj-fact"><span>Leads needed / month</span><strong>{num(D.target.leads)}</strong></div>
          </div>
        </div>
      </Panel>

      <Sub note="Targets are reverse-engineered from the $40M goal. Each tile shows its data source.">Live performance dashboard</Sub>
      <div className="egt-tiles">{tiles.map((t, i) => <Tile key={t.label} {...t} delay={i * 50} testid={`egt-tile-${i}`} />)}</div>

      {S.mode !== 'live' && (
        <>
          <Sub note={S.mode === 'example' ? 'Editing switches you to Manual mode.' : 'Your actuals — saved to your workspace.'}>Current performance</Sub>
          <Panel testid="egt-current-panel">
            <div className="egt-ctrls">
              <Slider label="Current lodgements / month" value={S.cur.lodgeM} min={0} max={60} fmt={(v) => `$${v}M`} onChange={(v) => update({ mode: 'manual', cur: { ...S.cur, lodgeM: v } })} tone="var(--green)" testid="egt-cur-lodge" />
              <NumInput label="Current incoming leads / month" value={S.cur.leads} onChange={(v) => update({ mode: 'manual', cur: { ...S.cur, leads: v } })} testid="egt-cur-leads" />
              <Slider label="Current portal completion" value={S.cur.portal} min={0} max={100} fmt={(v) => `${v}%`} onChange={(v) => update({ mode: 'manual', cur: { ...S.cur, portal: v } })} tone="var(--cyan)" testid="egt-cur-portal" />
            </div>
          </Panel>
        </>
      )}
      {S.mode === 'live' && live && (
        <div className="egt-note" data-testid="egt-live-note">Live · {live.month_label}: {live.lodged_count} lodgements ({money(live.lodged_amount)}), {live.leads} new leads, {live.portals_completed} portals completed{live.portal != null ? ` (${Math.round(live.portal)}% completion)` : ''}. Pulled from the Game Plan Tracker, Lead Nurture and Client Portal at {new Date(live.as_at).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}.</div>
      )}

      <div className="gpj-grid gpj-two" style={{ marginTop: 30 }}>
        <Panel title="Next critical actions" sub="Ranked by impact on the $40M target" testid="egt-actions">
          {D.actions.length ? (
            <ul className="gpf-list gpf">
              {D.actions.map((a, i) => (
                <li key={a.title} className={`gpf-row t-${a.tone}`} style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="gpf-n">{String(i + 1).padStart(2, '0')}</div>
                  <div className="gpf-b"><b>{a.title}</b><small>{a.body}</small><div className="gpf-bar"><i style={{ width: `${Math.round(Math.max(0, Math.min(1, a.p)) * 100)}%` }} /></div></div>
                </li>
              ))}
            </ul>
          ) : <div className="egt-trig t-green"><i><CheckCircle2 /></i><div><b>No critical actions</b><span>All tracked metrics are at or above plan.</span></div></div>}
        </Panel>
        <Panel title="Intelligent triggers" sub="Computed from your inputs and assumptions" testid="egt-triggers">
          <div className="egt-trigs">
            {D.triggers.map((t, i) => (
              <div key={t.title} className={`egt-trig t-${t.tone}`} style={{ animationDelay: `${i * 80}ms` }}>
                <i>{t.tone === 'green' ? <CheckCircle2 /> : t.tone === 'red' ? <AlertTriangle /> : <CircleAlert />}</i>
                <div><b>{t.title}</b><span>{t.body}</span></div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
